import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Download, Loader2, XCircle, Flower2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isCancel = pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [isCancel, sessionId]);

  return (
    <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center px-4 grain-overlay relative" data-testid="payment-result-page">
      <div className="max-w-md w-full bg-[#FAF7F0] border border-[#1C1E1D]/10 rounded-2xl p-10 text-center shadow-[0_20px_50px_-15px_rgba(28,30,29,0.12)]">
        <span className="inline-flex w-11 h-11 rounded-full bg-[#1C211E] text-[#F5F3ED] items-center justify-center mb-6">
          <Flower2 size={18} strokeWidth={1.5} />
        </span>
        {status === "checking" && (
          <>
            <Loader2 size={32} className="animate-spin text-[#C85A32] mx-auto" />
            <h1 className="font-serif text-2xl mt-5 text-[#1C1E1D]">Conferma del pagamento...</h1>
            <p className="text-sm text-[#525754] mt-3">Sto verificando la transazione con Stripe. Attendi qualche secondo.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={40} className="text-[#4A6B5D] mx-auto" />
            <h1 className="font-serif text-2xl mt-5 text-[#1C1E1D]" data-testid="payment-success-title">Grazie per il tuo acquisto</h1>
            <p className="text-sm text-[#525754] mt-3">Il pagamento è andato a buon fine. Il tuo PDF è pronto per il download.</p>
            <a href={`${API}/payments/download/${sessionId}`} data-testid="payment-download-button"
               className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-[#FBF9F5] text-sm font-medium px-7 py-3.5 transition-colors w-full">
              <Download size={16} /> Scarica il tuo PDF
            </a>
          </>
        )}
        {status === "cancelled" && (
          <>
            <XCircle size={40} className="text-[#C85A32] mx-auto" />
            <h1 className="font-serif text-2xl mt-5 text-[#1C1E1D]" data-testid="payment-cancel-title">Pagamento annullato</h1>
            <p className="text-sm text-[#525754] mt-3">Nessun addebito è stato effettuato. Puoi riprovare quando vuoi.</p>
            <Link to="/" data-testid="payment-retry-link"
                  className="mt-7 inline-flex items-center justify-center rounded-full bg-[#1C211E] hover:bg-[#C85A32] text-[#F5F3ED] text-sm font-medium px-7 py-3.5 transition-colors w-full">
              Torna alle risorse
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 size={32} className="text-[#8A908C] mx-auto" />
            <h1 className="font-serif text-2xl mt-5 text-[#1C1E1D]">Verifica in corso</h1>
            <p className="text-sm text-[#525754] mt-3">La conferma sta impiegando più del previsto. Se hai completato il pagamento, riceverai il link via email.</p>
            <Link to="/" className="mt-7 inline-flex text-sm text-[#C85A32] hover:underline">Torna alla home</Link>
          </>
        )}
      </div>
    </div>
  );
}
